import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { InputControlBase } from '../../forms/input-control.base';
import { AstralFieldShellComponent } from '../astral-field-shell/astral-field-shell.component';
import { AstralFieldActionComponent } from '../astral-field-action/astral-field-action.component';
import { resolveAstralIconClass } from '../astral-icon-class.util';
import { AstralFieldAccessibilityDirective } from '../astral-field-accessibility.util';

@Component({
  selector: 'astral-masked-input',
  standalone: true,
  imports: [NgTemplateOutlet, ReactiveFormsModule, TranslatePipe, AstralFieldShellComponent, AstralFieldActionComponent, AstralFieldAccessibilityDirective],
  templateUrl: './astral-masked-input.component.html',
  styleUrls: ['./astral-masked-input.component.scss'],
})
export class AstralMaskedInputComponent extends InputControlBase implements AfterViewInit {
  private static nextId = 0;
  private readonly uid = `astral-masked-input-${++AstralMaskedInputComponent.nextId}`;

  constructor(private readonly cd: ChangeDetectorRef) {
    super();
  }

  @Input() withShell = true;
  @Input() autocomplete = 'off';
  @Input() clearOthers = false;
  @Input() filterActions = false;
  @Input() doNotDisplayClear = false;
  @Input() inputMode: 'text' | 'numeric' | 'decimal' | 'tel' | 'email' | 'url';
  @Input() inputId: string;
  @Input() inputName: string;
  @Input() mask: RegExp;
  @Input() maskFormat: string;
  @Input() placeholderText: string;
  @Input() span: number | 'full' = 1;
  @Input() supplementalLabel: string;
  @Input() textAlignCenter = false;
  @Input() focused = false;

  @Output() readonly addonClick = new EventEmitter();

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  @HostBinding('tabindex') tabindex = -1;

  isFocused = false;
  private activePlaceholderText: string | null = null;

  @HostBinding('style.grid-column')
  get hostGridColumn(): string | null {
    if (this.span === 'full') {
      return '1 / -1';
    }

    if (!this.span || this.span <= 1) {
      return null;
    }

    return `span ${this.span}`;
  }

  get resolvedInputId(): string {
    return this.inputId ?? this.uid;
  }

  get resolvedInputName(): string {
    if (this.inputName) {
      return this.inputName;
    }

    const caption = this.control?.caption
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return caption || this.uid;
  }

  get resolvedPlaceholderText(): string | null {
    return this.activePlaceholderText ?? this.placeholderText ?? null;
  }

  get resolvedStringValue(): string {
    if (this.control?.value === undefined || this.control?.value === null) {
      return '';
    }

    return String(this.control.value).trim();
  }

  get resolvedSupplementalLabel(): string | null {
    return this.supplementalLabel ?? this.control?.supplementalLabel ?? null;
  }

  get readonlyIconClass(): string {
    return resolveAstralIconClass('lock') ?? '';
  }

  get showClearOthers(): boolean {
    const parentControls = this.control?.parent?.controls;

    if (!this.clearOthers || !parentControls) {
      return false;
    }

    return Object.keys(parentControls).length > 1 && (this.filterActions || !!this.resolvedStringValue);
  }

  get showClearThis(): boolean {
    return !this.control?.isReadonly && !this.doNotDisplayClear && (this.filterActions || !!this.resolvedStringValue);
  }

  @HostListener('focus')
  focusHost() {
    this.input?.nativeElement.focus();
  }

  ngAfterViewInit(): void {
    if (this.focused) {
      this.input?.nativeElement.focus();
      this.isFocused = true;
      this.cd.detectChanges();
    }
  }

  handleBlur() {
    this.isFocused = false;
    this.activePlaceholderText = null;
    this.onblur();
  }

  handleFocus() {
    this.isFocused = true;
    this.activePlaceholderText = this.maskFormat ?? null;
    this.onfocus();
  }

  handleInput(value: string): void {
    if (!this.control) {
      return;
    }

    if (!this.mask) {
      this.control.setValue(value || null);
      return;
    }

    const match = this.mask.exec(value);
    this.control.setValue(match ? match[0] : null);
  }

  clearAllOtherInputs(): boolean {
    const parentControls = this.control?.parent?.controls;
    if (!parentControls) {
      return false;
    }

    for (const controlName of Object.keys(parentControls)) {
      const currentControl = this.control.parent.get(controlName);
      if (currentControl !== this.control) {
        currentControl.setValue(undefined);
      }
    }

    this.control.parent.markAsDirty();
    this.addonClick.emit(this.control);
    return false;
  }

  clearInput(): boolean {
    if (!this.control) {
      return false;
    }

    this.onfocus();
    this.control.setValue(undefined);

    let form = this.control.parent;
    while (form?.parent) {
      form = form.parent;
    }

    form?.markAsDirty();
    this.onblur();
    this.addonClick.emit(this.control);
    return false;
  }
}
